import { useState, useEffect } from "react";
import { rootDoc } from "./firebase";
import { getDoc, setDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import "./GuestForm.css";

const WEDDING_DATE = new Date("2026-09-03T12:00:00");
const COUPLE_NAMES = "Keith & Maggy's";

export default function GuestForm() {
  const [formData, setFormData] = useState({
    title: "Mr",
    name: "",
    surname: "",
    email: "",
    phone: "",
    behalfOf: "Keith",
  });
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Monitor online status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isOnline) {
      setErrorMessage("You appear to be offline. Please check your internet connection and try again.");
      setShowErrorModal(true);
      return;
    }

    setIsSubmitting(true);

    try {
      const email = formData.email.trim().toLowerCase();
      const localPart = email.split('@')[0];
      const domain = email.split('@')[1];

      const rolePrefixes = ['admin', 'info', 'support', 'noreply', 'contact', 'mailer-daemon', 'postmaster'];
      if (rolePrefixes.includes(localPart)) {
        setErrorMessage('Please use a personal email address, not a role-based one.');
        setShowErrorModal(true);
        return;
      }

      const disposableDomains = ['mailinator.com', 'guerrillamail.com', '10minutemail.com', 'tempmail.com', 'throwaway.email', 'yopmail.com', 'sharklasers.com', 'trashmail.com'];
      if (disposableDomains.includes(domain)) {
        setErrorMessage('Disposable email addresses are not allowed.');
        setShowErrorModal(true);
        return;
      }

      const commonDomains = { 'gamil.com': 'gmail.com', 'gmial.com': 'gmail.com', 'yaho.com': 'yahoo.com', 'yahooo.com': 'yahoo.com', 'hotmai.com': 'hotmail.com', 'hotmal.com': 'hotmail.com', 'outlok.com': 'outlook.com' };
      if (commonDomains[domain]) {
        setErrorMessage(`Did you mean ${commonDomains[domain]}?`);
        setShowErrorModal(true);
        return;
      }

      const dnsRes = await fetch(`https://dns.google/resolve?name=${encodeURIComponent(domain)}&type=MX`);
      const dnsData = await dnsRes.json();
      const hasMxRecords = dnsData.Answer && dnsData.Answer.some(r => r.type === 15);
      if (!hasMxRecords) {
        setErrorMessage('Invalid or disposable email address. Please use a real email.');
        setShowErrorModal(true);
        return;
      }

      formData.email = email;
      const guestListRef = rootDoc("guests", "guestList");
      const guestListDoc = await getDoc(guestListRef);

      let currentGuests = [];
      if (guestListDoc.exists()) {
        currentGuests = guestListDoc.data().guests || [];
      }

      const totalGuests = currentGuests.length;

      if (totalGuests >= 260) {
        setErrorMessage("Sorry, we've reached our maximum venue capacity. Please contact us if you have any questions.");
        setShowErrorModal(true);
        return;
      }

      if (!guestListDoc.exists()) {
        await setDoc(guestListRef, {
          guests: [formData]
        });
      } else {
        await updateDoc(guestListRef, {
          guests: arrayUnion(formData)
        });
      }

      setShowSuccessModal(true);
      setFormData({ title:"Mr", name: "", surname: "", email: "", phone: "", behalfOf: "Keith" });
    } catch (error) {
      console.error("Error submitting form:", error);
      setErrorMessage(
        error.code === 'unavailable' || error.code === 'failed-precondition'
          ? "Unable to connect to our servers. Please check your internet connection and try again."
          : "There was an error submitting your RSVP. Please try again later."
      );
      setShowErrorModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeSuccessModal = () => {
    setShowSuccessModal(false);
  };

  const closeErrorModal = () => {
    setShowErrorModal(false);
  };

  const today = new Date();
  const timeDiff = WEDDING_DATE - today;
  const daysLeft = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
  const isWeddingDay = daysLeft === 0 && today.toDateString() === WEDDING_DATE.toDateString();
  const isAfterWedding = today > WEDDING_DATE;
  const daysMarried = isAfterWedding ? Math.floor(Math.abs(timeDiff) / (1000 * 60 * 60 * 24)) : 0;

  const renderWeddingStatus = () => {
    if (isWeddingDay) {
      return (
        <div className="countdown-banner wedding-day-banner">
          <span className="days-counter">🎉 It's Our Wedding Day! 🎉</span>
        </div>
      );
    } else if (isAfterWedding) {
      return (
        <div className="countdown-banner married-banner">
          <span className="days-counter">Married since {daysMarried} day{daysMarried !== 1 ? 's' : ''}!</span>
        </div>
      );
    } else {
      return (
        <div className="countdown-banner">
          <span className="days-counter">{daysLeft}</span> days to go!
        </div>
      );
    }
  };
  
  return (
    <div className="guest-form-container">
      <div className="form-card">
        <h1 className="welcome-title">You're Invited!</h1>
        <h2 className="couple-names">{COUPLE_NAMES}</h2>
        <h2 className="couple-names">Wedding</h2>
        <div className="wedding-date">September 3, 2026 at 12:00 PM</div>

        {renderWeddingStatus()}
        
        <div className="welcome-message">
        <p>
          Welcome to our wedding registry!
          <br />
          We’re excited to celebrate our special day with you.
          <br />
          <br />
          Please complete the details below to RSVP and confirm your attendance.
          You’ll receive a confirmation email with access to our wedding website
          and event updates.
        </p>
      </div>
        {/* <div className="welcome-message"> */}
          {/* <p>Welcome to our wedding registry!</p> */}
          {/* <p>We’re excited to celebrate our special day with you.</p> */}
          {/* <p>Please complete the details below to RSVP and confirm your attendance. You’ll receive a confirmation email with access to our wedding website and event updates.</p> */}

          {/* <p>Welcome to our wedding registry! You have been officially invited to celebrate our special day with us.</p> */}
          {/* <p>Please provide your details below so that in the coming months, we can add you to our wedding webapp where you'll find all the information about our celebration.</p> */}
        {/* </div> */}

        <h3 className="form-subtitle">Guest Registration</h3>
        
        <form onSubmit={handleSubmit}>
        <select 
            name="title" 
            value={formData.title} 
            onChange={handleChange} 
            className="input-field"
          >
            <option value="Mr">Mr</option>
            <option value="Mrs">Mrs</option>
            <option value="Ms">Ms</option>
          </select>
          <input 
            name="name" 
            placeholder="Name" 
            value={formData.name} 
            onChange={handleChange} 
            className="input-field" 
            required 
          />
          <input 
            name="surname" 
            placeholder="Surname" 
            value={formData.surname} 
            onChange={handleChange} 
            className="input-field" 
            required 
          />
          <input 
            type="email" 
            name="email" 
            placeholder="Email" 
            value={formData.email} 
            onChange={handleChange} 
            className="input-field" 
            required 
          />
          <input 
            type="tel" 
            name="phone" 
            placeholder="Phone Number" 
            value={formData.phone} 
            onChange={handleChange} 
            className="input-field" 
            required 
          />
          <div className="side-selection">
          <p className="selection-label">Who invited you?</p>

          <label
            className={`side-card ${formData.behalfOf === "Keith" ? "active" : ""}`}
          >
            <input
              type="radio"
              name="behalfOf"
              value="Keith"
              checked={formData.behalfOf === "Keith"}
              onChange={handleChange}
            />
            <span>Keith’s Side</span>
          </label>

          <label
            className={`side-card ${formData.behalfOf === "Maggy" ? "active" : ""}`}
          >
            <input
              type="radio"
              name="behalfOf"
              value="Maggy"
              checked={formData.behalfOf === "Maggy"}
              onChange={handleChange}
            />
            <span>Maggy’s Side</span>
          </label>
        </div>
          <button 
            type="submit" 
            className="submit-button" 
            disabled={isSubmitting || isAfterWedding}
          >
            {isSubmitting ? "Submitting..." : isAfterWedding ? "Wedding Complete" : "RSVP"}
          </button>
        </form>
        
        <div className="thank-you-message">
          We look forward to celebrating with you!
        </div>
        
        {!isOnline && (
          <div className="offline-notice">
            You appear to be offline. Please connect to the internet to submit your RSVP.
          </div>
        )}
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="modal-overlay" onClick={closeSuccessModal}>
          <div className="success-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-icon">✓</div>
              <h3 className="modal-title">Thank You!</h3>
              <p className="modal-message">Your RSVP has been received.</p>
              <p className="modal-message">We can't wait to celebrate our special day with you!</p>
              <button onClick={closeSuccessModal} className="modal-button">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="modal-overlay" onClick={closeErrorModal}>
          <div className="error-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-icon error">!</div>
              <h3 className="modal-title">Oops!</h3>
              <p className="modal-message">{errorMessage}</p>
              <button onClick={closeErrorModal} className="modal-button">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}